import React, { useMemo, useCallback } from 'react';
import { Box, Paper, IconButton, Button } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx } from '../../theme/tokens';
import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm5_u1_bacnet';

const images = [
  { key: 'bacnet_symbol', src: '/assets/drawable/bacnet_symbol.png', label: 'BACnet Symbol' }
];

const textBlocks = [
    { key: 'block_intro', heading: 'BACnet', body: 'BACnet (Building Automation and Control Networks) ist ein weitverbreitetes Kommunikationsprotokoll für die Gebäudeautomation. Seine Hauptaufgabe ist die Vernetzung verschiedener Systeme wie Heizung, Lüftung, Klimatechnik (HLK), Beleuchtung, Zutrittskontrolle und Brandschutzsysteme innerhalb eines Gebäudes, um eine integrierte und effiziente Steuerung zu ermöglichen.' },
    { key: 'block_features', heading: '', body: 'BACnet ist ein offener Standard (ANSI/ASHRAE 135, ISO 16484-5) und ermöglicht die Interoperabilität von Geräten und Systemen unterschiedlicher Hersteller. Dies fördert Flexibilität, Wettbewerb und langfristige Investitionssicherheit.' },
    { key: 'block_technical', heading: 'Technische Details und Funktionsweise', body: 'BACnet nutzt ein Objekt-orientiertes Datenmodell. Geräte und Datenpunkte werden als Objekte mit Eigenschaften definiert (z.B. Analogeingänge, Binärausgänge). Verschiedene Dienste (Services) definieren, wie Geräte miteinander kommunizieren (Lesen, Schreiben, Ereignismeldungen, Alarme etc.).' },
    { key: 'block_components', heading: 'Wichtige BACnet-Komponenten', body: '- BACnet-Geräte: Regler, Sensoren, Aktoren, die BACnet "sprechen".\n- BACnet-Netzwerke: Können über verschiedene Medien laufen: Ethernet (BACnet/IP), MS/TP (Master-Slave/Token-Passing über RS-485), und andere.\n- BACnet-Gateways/Router: Verbinden verschiedene BACnet-Netzwerke oder integrieren Nicht-BACnet-Geräte (z.B. über Modbus, KNX).' },
    { key: 'block_advantages', heading: 'Vorteile von BACnet', body: '- Offener Standard: Interoperabilität zwischen Herstellern.\n- Flexibilität: Unterstützt verschiedene Netzwerkarchitekturen und -medien.\n- Skalierbarkeit: Von kleinen bis sehr großen Anlagen.\n- Umfangreiche Funktionalität: Lesen/Schreiben von Werten, Alarme, Zeitpläne, Trendaufzeichnungen.\n- Weitverbreitet: Große Anzahl an kompatiblen Geräten und Systemen weltweit.' }
];

const M5_U1_BACnet: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM5, canUploadImagesM5 } = useAuth();
  const canEditTexts = canEditTextsM5;
  const canUploadImages = canUploadImagesM5;
  const { darkMode } = useTheme();

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = useMemo(
    () => textBlocks.map(tb => ({ ...tb, body: override?.texts?.[tb.key] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return alert('Kein Token.');
    if (!canEditTexts) return alert('Keine Berechtigung zum Texte bearbeiten.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      console.error('Text-Speichern fehlgeschlagen:', e);
      alert('Fehler beim Speichern.');
    }
  }, [token, refresh, canEditTexts]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token.');
    if (!canUploadImages) return alert('Keine Berechtigung fuer Bilder-Uploads.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (e: any) {
      console.error('Bild-Upload fehlgeschlagen:', e);
      alert('Fehler beim Upload.');
    }
  }, [token, refresh, canUploadImages]);

  const ttsBtn = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '5.1 BACnet', actions: ttsBtn },
    React.createElement(Paper, { sx: getCardSx(darkMode), elevation: 2 },
      React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, images.map(img => React.createElement(ZoomableImage, { key: img.key, src: imgSrc(img.key, img.src), label: img.label, onReplace: canUploadImages ? (f: File) => handleImageReplace(img.key, f) : undefined })) ),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } }, mergedBlocks.map(tb => React.createElement(EditableTextBlock, { key: tb.key, heading: tb.heading, body: tb.body, onSave: canEditTexts ? (v: string) => handleTextSave(tb.key, v) : undefined, pageId: PAGE_ID, blockId: tb.key })) )
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m5_u99_quiz') }, 'Zum Quiz')
    )
  );
};

export default M5_U1_BACnet;

